import { portalText } from "../../i18n/portal.js";
/**
 * Area `billing-profile` of the console gateway — over `apps/api/src/routes/billing-profile/` (design §27.9.2). Owned by
 * plan-014 unit **D5a**.
 *
 *   readBillingProfile         `readBillingProfile`         the legal and tax details in force (null before any)
 *   saveBillingProfile         `replaceBillingProfile`      new details from now; a new EU business number is checked
 *
 * No "Check again" (owner answer 42, 2026-10-06): the number is checked once when it is entered or changed; a recheck
 * is an operator's, from the staff portal.
 *
 * ── THE GENERATED CLIENT ─────────────────────────────────────────────────────────────────────────────────────
 * The two operations are in D5a's fragments; the merged spec and `@wetalk/api-client`'s generated client are
 * regenerated by the api-client lock holder (plan-014 §2.3), never by a unit. So this slice reads them through
 * {@link operationsOf}, a structural view of the client with exactly these methods (C1's pattern): it compiles before
 * and after the regeneration, and a client that still lacks one raises naming the regeneration — never an empty answer.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readTextArray } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

export type PayerKindView = "business" | "consumer";
export type TaxIdStatusView = "unchecked" | "valid" | "invalid" | "pending" | "unavailable";

/** How VAT applies to the next document (the rate is read from the VAT rate table at issuance). */
export type VatTreatmentView =
  | { readonly kind: "domestic_greece" }
  | { readonly kind: "reverse_charge" }
  | { readonly kind: "destination_consumer"; readonly country: string; readonly reason: "consumer" | "tax_id_not_valid" }
  | { readonly kind: "outside_scope" };

export interface BillingProfileView {
  readonly billingProfileId: string;
  readonly legalName: string;
  readonly tradingName: string | null;
  readonly payerKind: PayerKindView;
  readonly country: string;
  readonly addressLine: readonly string[];
  readonly postalCode: string | null;
  readonly city: string;
  readonly taxId: string | null;
  readonly billingEmail: readonly string[];
  readonly accountingReference: string | null;
  readonly currency: string;
  readonly appliesFrom: string;
  readonly taxIdStatus: TaxIdStatusView;
  /** Whether the EU VAT register checks this payer's number (an EU business). */
  readonly taxIdChecked: boolean;
  readonly checkedAt: string | null;
  readonly consultationNumber: string | null;
  readonly vatTreatment: VatTreatmentView;
}

/** What the form sends. */
export interface BillingProfileInput {
  readonly legalName: string;
  readonly tradingName: string | null;
  readonly payerKind: PayerKindView;
  readonly country: string;
  readonly addressLine: readonly string[];
  readonly postalCode: string | null;
  readonly city: string;
  readonly taxId: string | null;
  readonly billingEmail: readonly string[];
  readonly accountingReference: string | null;
  /** The details the form was showing (`null`: none) — a save over someone else's is `409 revision_stale`. */
  readonly expectedBillingProfileId: string | null;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** The organization's legal and tax details in force, or `null` before any are given. */
    readBillingProfile(accountId: string): Promise<BillingProfileView | null>;
    /** Saves new details from now; a new EU business VAT number is checked with the EU VAT register. */
    saveBillingProfile(accountId: string, input: BillingProfileInput): Promise<BillingProfileView>;
  }
}

export type BillingProfileGatewaySlice = Pick<ConsoleGateway, "readBillingProfile" | "saveBillingProfile">;

/** This area's rows of `GATEWAY_GAP`: none — every operation is in D5a's fragments. */
export const billingProfileGap: readonly GatewayGap[] = [];

// ── the client, structurally ──────────────────────────────────────────────────────────────────────────────

interface BillingProfileOperations {
  readBillingProfile(request: { path: { account_id: string } }): Promise<ApiResult>;
  replaceBillingProfile(request: { path: { account_id: string }; body?: unknown }): Promise<ApiResult>;
}

/** The client's methods for this area, by name. Raises for one the generated client does not carry yet. */
function operationsOf(client: WeTalkApiClient): BillingProfileOperations {
  const held = client as unknown as Partial<Record<keyof BillingProfileOperations, unknown>>;
  return new Proxy({} as BillingProfileOperations, {
    get(_target, name: string) {
      const found = held[name as keyof BillingProfileOperations];
      if (typeof found !== "function") {
        throw new Error(
          portalText("shell", "the_generated_api_client_has_no_value_regenerate_it_pnpm_a_7e252942", { v0: String(name) }),
        );
      }
      return (found as (...argument: unknown[]) => unknown).bind(client);
    },
  });
}

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

function textOrNull(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

const PAYER_KIND: readonly PayerKindView[] = ["business", "consumer"];
const TAX_ID_STATUS: readonly TaxIdStatusView[] = ["unchecked", "valid", "invalid", "pending", "unavailable"];

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const found = allowed.find((one) => one === value);
  if (found === undefined) throw new Error(`${path}: ${JSON.stringify(value)} is not one of ${allowed.join(", ")}`);
  return found;
}

function treatmentOf(value: unknown, path: string): VatTreatmentView {
  const one = readRecord(value, path);
  const kind = readText(one["kind"], `${path}.kind`);
  if (kind === "domestic_greece" || kind === "reverse_charge" || kind === "outside_scope") return { kind };
  if (kind === "destination_consumer") {
    return {
      kind,
      country: readText(one["country"], `${path}.country`),
      reason: oneOf(["consumer", "tax_id_not_valid"] as const, one["reason"], `${path}.reason`),
    };
  }
  throw new Error(`${path}.kind: ${JSON.stringify(kind)} is not a VAT treatment this console knows`);
}

export function billingProfileOfWire(data: Readonly<Record<string, unknown>>, path: string): BillingProfileView {
  return {
    billingProfileId: readText(data["billing_profile_id"], `${path}.billing_profile_id`),
    legalName: readText(data["legal_name"], `${path}.legal_name`),
    tradingName: textOrNull(data["trading_name"], `${path}.trading_name`),
    payerKind: oneOf(PAYER_KIND, data["payer_kind"], `${path}.payer_kind`),
    country: readText(data["country"], `${path}.country`),
    addressLine: readTextArray(data["address_line"], `${path}.address_line`),
    postalCode: textOrNull(data["postal_code"], `${path}.postal_code`),
    city: readText(data["city"], `${path}.city`),
    taxId: textOrNull(data["tax_id"], `${path}.tax_id`),
    billingEmail: readArray(data["billing_email"], `${path}.billing_email`).map((one, at) => readText(one, `${path}.billing_email[${at}]`)),
    accountingReference: textOrNull(data["accounting_reference"], `${path}.accounting_reference`),
    currency: readText(data["currency"], `${path}.currency`),
    appliesFrom: readText(data["applies_from"], `${path}.applies_from`),
    taxIdStatus: oneOf(TAX_ID_STATUS, data["tax_id_status"], `${path}.tax_id_status`),
    taxIdChecked: readBoolean(data["tax_id_checked"], `${path}.tax_id_checked`),
    checkedAt: textOrNull(data["checked_at"], `${path}.checked_at`),
    consultationNumber: textOrNull(data["consultation_number"], `${path}.consultation_number`),
    vatTreatment: treatmentOf(data["vat_treatment"], `${path}.vat_treatment`),
  };
}

export function billingProfileBodyOf(input: BillingProfileInput): Readonly<Record<string, unknown>> {
  return {
    legal_name: input.legalName,
    trading_name: input.tradingName,
    payer_kind: input.payerKind,
    country: input.country,
    address_line: input.addressLine,
    postal_code: input.postalCode,
    city: input.city,
    tax_id: input.taxId,
    billing_email: input.billingEmail,
    accounting_reference: input.accountingReference,
    expected_billing_profile_id: input.expectedBillingProfileId,
  };
}

function dataOf(result: ApiResult, operationId: string): unknown {
  return readRecord(assertOk(result), operationId)["data"];
}

export function billingProfileApiSlice(client: WeTalkApiClient, _context: SliceContext): BillingProfileGatewaySlice {
  const operations = operationsOf(client);
  return {
    async readBillingProfile(accountId) {
      const data = dataOf(await operations.readBillingProfile({ path: { account_id: accountId } }), "readBillingProfile");
      return data === null ? null : billingProfileOfWire(readRecord(data, "readBillingProfile.data"), "readBillingProfile.data");
    },
    async saveBillingProfile(accountId, input) {
      const data = dataOf(
        await operations.replaceBillingProfile({ path: { account_id: accountId }, body: billingProfileBodyOf(input) }),
        "replaceBillingProfile",
      );
      return billingProfileOfWire(readRecord(data, "replaceBillingProfile.data"), "replaceBillingProfile.data");
    },
  };
}
